import React, { useState, useRef } from 'react';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { selectProjectPath, selectBranch } from 'app/sharedSlice';
import {
  selectApplication,
  setApplication,
  selectProcessingFrequency,
  selectGradCode,
  setGradCode,
  selectLineOfBusinessOwningDP,
  selectDataDomain,
  selectDerivedBusinessUseCase,
  selectUrl,
  setUrl,
  selectSourceSystem,
  setSourceSystem,
  selectSpecification1,
  setSpecification1,
  selectPurpose,
  setPurpose,
  selectBusinessTerm,
  setBusinessTerm,
  selectBusinessTermDefinition,
  setBusinessTermDefinition,
  selectName,
  selectDescription,
  selectVersion,
  selectJiraTicketNo,
  setJiraTicketNo,
  selectDataProductManager,
  selectVersionDescription,
  selectSchemaDescription,
  selectContainsPatientData,
  selectContainsPersonalData,
  selectIsGxpValidated,
  selectIsSoxControlled,
  selectDataProductCategory,
  selectDataProductType,
  setDataProductType,
  selectSecurityClassification,
  selectDerivedProgram,
  selectRelationships,
} from '../reducers/astraZenecaSlice';
import { useAstraZenecaMetadataMutation } from 'app/createApi';
import StepWrapper from 'components/StepWrapper';
import DropdownSingleselect from 'components/DropdownSingleselect';
import DividerWithText from 'components/DividerWithText';
import LoadingAndErrorSection from 'components/LoadingAndErrorSection';

export interface DataProductDefinitionStepProps {
  onBack: () => void;
  onContinue: () => void;
}

export default function DataProductDefinition2Step(props: DataProductDefinitionStepProps): JSX.Element {
  const dispatch = useAppDispatch();

  const projectName = useAppSelector(selectProjectPath);
  const branch = useAppSelector(selectBranch);

  const application = useAppSelector(selectApplication);
  const processingFrequency = useAppSelector(selectProcessingFrequency);
  const gradCode = useAppSelector(selectGradCode);
  const lineOfBusinessOwningDP = useAppSelector(selectLineOfBusinessOwningDP);
  const dataDomain = useAppSelector(selectDataDomain);
  const businessUseCase = useAppSelector(selectDerivedBusinessUseCase);
  const url = useAppSelector(selectUrl);
  const businessTerm = useAppSelector(selectBusinessTerm);
  const businessTermDefinition = useAppSelector(selectBusinessTermDefinition);

  const name = useAppSelector(selectName);
  const description = useAppSelector(selectDescription);
  const version = useAppSelector(selectVersion);
  const jiraTicketNo = useAppSelector(selectJiraTicketNo);
  const dataProductManager = useAppSelector(selectDataProductManager);
  const versionDescription = useAppSelector(selectVersionDescription);
  const schemaDescription = useAppSelector(selectSchemaDescription);
  const dataProductCategory = useAppSelector(selectDataProductCategory);
  const dataProductType = useAppSelector(selectDataProductType);
  const containsPatientData = useAppSelector(selectContainsPatientData);
  const containsPersonalData = useAppSelector(selectContainsPersonalData);
  const isGxpValidated = useAppSelector(selectIsGxpValidated);
  const isSoxControlled = useAppSelector(selectIsSoxControlled);
  const purpose = useAppSelector(selectPurpose);
  const specification1 = useAppSelector(selectSpecification1);
  const sourceSystem = useAppSelector(selectSourceSystem);
  const securityClassification = useAppSelector(selectSecurityClassification);
  const program = useAppSelector(selectDerivedProgram);
  const relationships = useAppSelector(selectRelationships);

  const [loading, setLoading] = useState<boolean>(false);
  const [failed, setFailed] = useState<boolean>(false);
  const [errorMessage] = useState<string>(
    'Oops! Failed to save the data product attributes. Please try again or contact support@dataops.live if the issue persists.',
  );

  const [astraZenecaMetadata] = useAstraZenecaMetadataMutation();

  const formRef = useRef<HTMLFormElement>(null);

  const onContinue = (): void => {
    if (formRef.current?.reportValidity() === true) {
      setLoading(true);
      setFailed(false);

      astraZenecaMetadata({
        projectName,
        branch,
        name,
        description,
        jiraTicketNo,
        dataProductManager,
        version,
        versionDescription,
        schemaDescription,
        dataProductCategory,
        dataProductType,
        containsPatientData,
        containsPersonalData,
        isGxpValidated,
        isSoxControlled,
        purpose,
        specification1,
        sourceSystem,
        application,
        processingFrequency,
        gradCode,
        lineOfBusinessOwningDP,
        dataDomain,
        relationships,
        url,
        businessTerm,
        businessTermDefinition,
        securityClassification,
      })
        .unwrap()
        .then((res: any) => {
          props.onContinue();
        })
        .catch((err) => {
          console.log(err);
          setFailed(true);
          // Currently we use a generic error message and not the one returned by the backend
          // setErrorMessage(err.data);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return (
    <StepWrapper
      title="Extended Data Product attributes"
      subtitle="Fields marked with * are required"
      onBack={() => props.onBack()}
      onContinue={() => onContinue()}
      isLoading={loading}
      continueDisabled={loading || program === '' || businessUseCase === ''}
    >
      <div className="h-full">
        <form ref={formRef}>
          <div className="mx-[auto] w-3/4 px-2 pb-6 grid grid-cols-2 gap-6">
            <div className="col-span-2 mt-2">
              <DividerWithText dividerText="Optional attributes" />
            </div>

            <div className="col-span-2 xl:col-span-1">
              <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                JIRA Ticket No
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300">
                  <input
                    type="text"
                    value={jiraTicketNo}
                    onChange={(e) => dispatch(setJiraTicketNo(e.target.value))}
                    name="name"
                    id="name"
                    autoComplete="name"
                    className="block flex-1 border-0 bg-transparent p-1.5 text-black placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>

            <div className="col-span-2 xl:col-span-1">
              <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                Purpose
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 bg-white">
                  <input
                    type="text"
                    value={purpose}
                    onChange={(e) => dispatch(setPurpose(e.target.value))}
                    name="name"
                    id="name"
                    autoComplete="name"
                    className="block flex-1 border-0 bg-transparent p-1.5 text-black placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>

            <div className="col-span-2 xl:col-span-1">
              <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                Source system
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 bg-white">
                  <input
                    type="text"
                    value={sourceSystem}
                    onChange={(e) => dispatch(setSourceSystem(e.target.value))}
                    name="name"
                    id="name"
                    autoComplete="name"
                    className="block flex-1 border-0 bg-transparent p-1.5 text-black placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>

            <div className="col-span-2 xl:col-span-1">
              <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                Business Term
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 bg-white">
                  <input
                    type="text"
                    value={businessTerm}
                    onChange={(e) => dispatch(setBusinessTerm(e.target.value))}
                    name="name"
                    id="name"
                    autoComplete="name"
                    className="block flex-1 border-0 bg-transparent p-1.5 text-black placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>

            <div className="col-span-2 xl:col-span-1">
              <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                Business Term Definition
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 bg-white">
                  <input
                    type="text"
                    value={businessTermDefinition}
                    onChange={(e) => dispatch(setBusinessTermDefinition(e.target.value))}
                    name="name"
                    id="name"
                    autoComplete="name"
                    className="block flex-1 border-0 bg-transparent p-1.5 text-black placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>

            <div className="col-span-2 xl:col-span-1">
              <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                URL
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 bg-white">
                  <input
                    type="text"
                    value={url}
                    onChange={(e) => dispatch(setUrl(e.target.value))}
                    name="name"
                    id="name"
                    autoComplete="name"
                    className="block flex-1 border-0 bg-transparent p-1.5 text-black placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>

            <div className="col-span-2 xl:col-span-1">
              <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">
                Specification 1
              </label>
              <div className="mt-2">
                <div className="flex rounded-md shadow-sm ring-1 ring-inset ring-gray-300 bg-white">
                  <input
                    type="text"
                    value={specification1}
                    onChange={(e) => dispatch(setSpecification1(e.target.value))}
                    name="name"
                    id="name"
                    autoComplete="name"
                    className="block flex-1 border-0 bg-transparent p-1.5 text-black placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            </div>

            <div className="col-span-2 xl:col-span-1">
              <DropdownSingleselect
                label="Data Product Type"
                options={['Master', 'Analytical', 'Transactional']}
                selected={dataProductType}
                setSelected={(value: string) => dispatch(setDataProductType(value))}
              />
            </div>

            <div className="col-span-2 mt-2">
              <DividerWithText dividerText="Read-only attributes" />
            </div>

            <div className="col-span-2 xl:col-span-1">
              <DropdownSingleselect
                label="Application"
                options={['Snowflake (PRD)']}
                selected={application}
                setSelected={(value: string) => dispatch(setApplication(value))}
                disabled={true}
              />
            </div>

            <div className="col-span-2 xl:col-span-1">
              <DropdownSingleselect
                label="GRAD Code"
                options={['GRAD - 0000']}
                selected={gradCode}
                setSelected={(value: string) => dispatch(setGradCode(value))}
                disabled={true}
              />
            </div>
          </div>
        </form>
        <LoadingAndErrorSection isLoading={loading} isFailed={failed} errorMessage={errorMessage} hideLoading={true} />
      </div>
    </StepWrapper>
  );
}
